import { useSubscription } from "@apollo/client";
import { AUDIT_SUBSCRIPTION } from "@/graphql/subscriptions";
import { useSalonStore } from "@/lib/store/salon";

interface UseAuditSubscriptionOptions {
  salonId?: string | null;
  entityId?: string;
  tableName?: string;
  enabled?: boolean;
}

/**
 * Hook to subscribe to real-time audit log changes
 *
 * @param options Configuration options for the audit subscription
 * @returns Subscription state and data
 */
export const useAuditSubscription = ({
  salonId,
  entityId,
  tableName,
  enabled = true,
}: UseAuditSubscriptionOptions = {}) => {
  const { currentSalon } = useSalonStore();

  // Handle passed value or fallback to store
  const finalSalonId = salonId !== undefined ? salonId : (currentSalon?.id ?? null);

  const { data, loading, error } = useSubscription(AUDIT_SUBSCRIPTION, {
    variables: {
      filters: {
        salonId: finalSalonId ?? "",
        ...(entityId && { entityId }),
        ...(tableName && { tableName }),
      },
    },
    skip: !enabled || !finalSalonId,
    onError: (_error) => {
      // Subscription errors are surfaced via the returned error state
    },
    onData: ({ data, client }) => {
      if (!data.data?.auditStream) {
        return;
      }

      const { auditEntry } = data.data.auditStream;

      // Update Apollo cache with new audit entry
      client.cache.modify({
        fields: {
          // Update getRecentChanges cache if it exists
          getRecentChanges: (existing) => {
            if (!existing) {
              return existing;
            }

            // Check if this entry should be included in recent changes
            const shouldInclude =
              (!entityId || auditEntry.entityId === entityId) &&
              (!tableName || auditEntry.tableName === tableName);

            if (!shouldInclude) return existing;

            // Check if entry already exists to prevent duplicates
            const existingEntries = existing.entries || [];
            const entryExists = existingEntries.some((entry: any) => entry.id === auditEntry.id);

            if (entryExists) {
              return existing;
            }

            // Add new entry to the beginning of the list
            const newEntries = [auditEntry, ...existingEntries];

            const updatedCache = {
              ...existing,
              entries: newEntries,
              totalCount: (existing.totalCount || 0) + 1,
            };

            return updatedCache;
          },
        },
      });
    },
  });

  return {
    data: data?.auditStream,
    loading,
    error,
  };
};
